<template>
  <FlareImage />
</template>

<script setup lang="ts">
  import { resolveDirective } from 'vue'

  interface FlarePictureProps extends FlareOptions {
    src: FlareImage
  }

  interface Source extends FlarePictureProps{
    minWidth?: Number
    breakpoint: String | Number
  }

  interface Src extends FlarePictureProps{
    requestLarger?: Boolean
  }

  const props = defineProps({
    src: {
      type: Object as PropType<{ [key: string | number]: Src }>
    },
    class: {
      type: String
    },
    lazy: {
      default: true,
      type: [Boolean, String]
    },
    dataNotLazy: {
      default: false,
      type: [Boolean, String]
    },
    alt: {
      default: '',
      type: [String, Object]
    },
    width: {
      type: [String, Number]
    },
    height: {
      type: [String, Number]
    }
  })
  
  const src = JSON.parse(JSON.stringify(props.src))
  const source: Array<Source> = []
  const isLazy = !(props.lazy === false || props.dataNotLazy !== false)
  const directives: Array<any> = isLazy ? [[resolveDirective('lazy-load')]] : []
  const rootAttrs: any = {
    class: `picture${props.class ? ` ${props.class}` : ''}`
  }
    
  for (const key in src) {
    const multiplier = [undefined, true].includes(src[key].requestLarger) ? 1.33 : 1

    for(const size of ['width', 'height']) {
      if (src[key][size]) src[key][size] = Math.ceil(Number(src[key][size]) * multiplier)
    }

    source.push({
      ...src[key],
      size: src[key].size || 'width',
      quality: src[key].quality || 90,
      format: src[key].format || 'auto',
      breakpoint: Number(key),
    })
  }

  const sources = source.sort((a, b) => {
    if (a.breakpoint < b.breakpoint) return 1
    if (a.breakpoint > b.breakpoint) return -1
    return 0
  })

  for (let i = 0; i < sources.length; i++) {
    if (sources[i + 1]) {
      const next = sources[i + 1]
      sources[i].minWidth = next ? Number(next.breakpoint) + 1 : 0
    }
  }

  const last = sources.pop() as Source
  const smallest = last.src ? last : sources[0]

  if(smallest?.src?.meta?.dominant_colors?.[0]) rootAttrs.style = { color: smallest.src.meta.dominant_colors[0] }

  const attrs = {
    width: (smallest.size !== 'both' ? smallest.src?.meta?.width : smallest.width) || props.width,
    height: (smallest.size !== 'both' ? smallest.src?.meta?.height : smallest.height) || props.height,
    lazy: isLazy,
  } as any

  if (props.alt) attrs.alt = typeof props.alt === 'object' ? props.alt.alt : props.alt

  const FlareImage = () => withDirectives(h('picture', rootAttrs, [
    sources.map(source => h('source', {
      media: `(min-width: ${source.minWidth}px)`,
      srcset: getSrcset(source.src, getFlareOptions(source))
    })),
    h(resolveComponent('Flare'), { ...attrs, ...smallest, requestLarger: false })
  ]), directives)
</script>
